import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { MessageSquare, Wind, Heart, LifeBuoy, Sparkles, ArrowRight } from 'lucide-react';
import { NewChatLauncher } from '../components/NewChatLauncher';

export default function Dashboard() {
    const { user } = useAuth();
    const { t, lang } = useT();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [moods, setMoods] = useState([]);
    const [launcherOpen, setLauncherOpen] = useState(false);

    useEffect(() => {
        api.get('/chat/sessions').then(r => setSessions(r.data)).catch(() => {});
        api.get('/mood').then(r => setMoods(r.data)).catch(() => {});
    }, []);

    const latest = sessions[0];

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-14" data-testid="dashboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-sm text-inkSoft">{t('dash.greeting', { name: user?.alias || '' })}</p>
                    <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-1">{t('dash.title')}</h1>
                </div>
                <Button onClick={() => setLauncherOpen(true)} className="rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="new-chat-cta">
                    <Sparkles className="w-4 h-4 mr-2"/> {t('dash.new.chat')}
                </Button>
            </div>

            <div className="mt-10 grid md:grid-cols-3 gap-4">
                <Card icon={MessageSquare} title={t('dash.continue.chat')} desc={latest ? latest.title : t('dash.continue.chat.empty')} onClick={() => latest ? navigate(`/app/samtale/${latest.session_id}`) : setLauncherOpen(true)} testid="card-continue" />
                <Card icon={Heart} title={t('dash.log.mood')} desc={t('dash.log.mood.desc')} onClick={() => navigate('/app/humor')} testid="card-mood" />
                <Card icon={Wind} title={t('dash.breathing')} desc={t('dash.breathing.desc')} onClick={() => navigate('/app/pust')} testid="card-breathing" />
            </div>

            <div className="mt-12 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-2xl border border-sand bg-white p-6">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-xl font-semibold">{t('dash.recent.chats')}</h2>
                        <button className="text-sm text-sage-dark hover:underline" onClick={() => navigate('/app/samtaler')} data-testid="all-chats-link">{t('dash.see.all')}</button>
                    </div>
                    <div className="mt-4 divide-y divide-sand">
                        {sessions.length === 0 && <p className="text-sm text-inkSoft py-4">{t('dash.empty.chats')}</p>}
                        {sessions.slice(0, 5).map(s => (
                            <button
                                key={s.session_id}
                                onClick={() => navigate(`/app/samtale/${s.session_id}`)}
                                className="w-full flex items-center justify-between py-3 hover:bg-sand/20 rounded-lg px-2 -mx-2 transition-colors text-left"
                                data-testid={`session-row-${s.session_id}`}
                            >
                                <div>
                                    <p className="font-medium text-ink">{s.title}</p>
                                    <p className="text-xs text-inkSoft">{new Date(s.updated_at).toLocaleString(lang === 'en' ? 'en-US' : 'nb-NO')}</p>
                                </div>
                                <ArrowRight className="w-4 h-4 text-inkSoft"/>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl border border-crisis/30 bg-crisis/5 p-6">
                    <div className="flex items-center gap-2 text-crisis">
                        <LifeBuoy className="w-5 h-5"/>
                        <h3 className="font-display font-semibold">{t('dash.crisis.title')}</h3>
                    </div>
                    <p className="text-sm text-inkSoft mt-3 leading-relaxed">
                        {lang === 'en' ? t('dash.crisis.body.en') : t('dash.crisis.body.no')}
                    </p>
                    <Button onClick={() => navigate('/app/ressurser')} className="mt-4 rounded-full bg-crisis hover:bg-crisis-dark text-white w-full" data-testid="crisis-btn">
                        {t('dash.crisis.btn')}
                    </Button>
                </div>
            </div>

            {moods.length > 0 && (
                <div className="mt-10 rounded-2xl border border-sand bg-white p-6">
                    <h2 className="font-display text-xl font-semibold">{t('dash.mood.recent')}</h2>
                    <div className="mt-4 flex gap-2 items-end">
                        {moods.slice(0, 14).reverse().map((m, i) => (
                            <div key={m.mood_id} className="flex flex-col items-center gap-1" title={new Date(m.created_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'nb-NO')}>
                                <div className={`w-4 h-4 rounded-full ${moodColor(m.score)}`} style={{ marginBottom: `${(m.score-1)*8}px` }}></div>
                                <span className="text-[10px] text-inkSoft">{new Date(m.created_at).getDate()}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <NewChatLauncher open={launcherOpen} onOpenChange={setLauncherOpen} />
        </div>
    );
}

function moodColor(s) {
    return ['bg-crisis', 'bg-[#E5A779]', 'bg-[#E8D9A0]', 'bg-[#A7C1A8]', 'bg-sage'][s-1];
}

function Card({ icon: Icon, title, desc, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="text-left rounded-2xl border border-sand bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 group">
            <div className="w-10 h-10 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark mb-3">
                <Icon className="w-5 h-5"/>
            </div>
            <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-ink">{title}</h3>
                <ArrowRight className="w-4 h-4 text-inkSoft group-hover:translate-x-0.5 transition-transform"/>
            </div>
            <p className="text-sm text-inkSoft mt-1 line-clamp-1">{desc}</p>
        </button>
    );
}
